import React from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  Image,
  Icon,
  Drawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FolderOpen,
  PaperPlaneTilt,
  House,
  SignOut,
  User,
  Receipt,
  Ticket,
  Columns,
} from 'phosphor-react';
import logo from '../../../assets/logo_dark_blue.png';

interface MenuItem {
  name: string;
  path: string;
  icon: React.ElementType;
}

interface SecaoClienteSidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  isMobile?: boolean;
}

const menuItems: MenuItem[] = [
  {
    name: 'Início',
    path: '/secao-cliente/dashboard',
    icon: House,
  },
  {
    name: 'Dossiê',
    path: '/secao-cliente/dossie',
    icon: FolderOpen,
  },
  {
    name: 'Guias e Impostos',
    path: '/secao-cliente/guias',
    icon: Receipt,
  },
  {
    name: 'Envio de Documentos',
    path: '/secao-cliente/documentos',
    icon: PaperPlaneTilt,
  },
  {
    name: 'Eventos',
    path: '/secao-cliente/eventos',
    icon: Ticket,
  },
  {
    name: 'Kanban',
    path: '/secao-cliente/kanban',
    icon: Columns,
  },
  {
    name: 'Meu Perfil',
    path: '/secao-cliente/perfil',
    icon: User,
  },
];

const SidebarContent = ({ onClose }: { onClose?: () => void }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    localStorage.removeItem('cliente_access_token');
    localStorage.removeItem('cliente_refresh_token');
    localStorage.removeItem('cliente_data');
    navigate('/secao-cliente/login');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (onClose) onClose();
  };

  const isActive = (path: string) => {
    if (path === '/secao-cliente/dossie') {
      return location.pathname.startsWith('/secao-cliente/dossie');
    }
    if (path === '/secao-cliente/kanban') {
      return location.pathname.startsWith('/secao-cliente/kanban');
    }
    return location.pathname === path;
  };

  return (
    <Flex direction="column" h="100%">
      {/* Logo */}
      <Flex
        align="center"
        justify="center"
        gap={3}
        p={6}
        borderBottom="1px solid"
        borderBottomColor="gray.100"
      >
        {(() => {
          try {
            const data = JSON.parse(localStorage.getItem('cliente_data') || '{}');
            if (data.empresa_logo_url) {
              return (
                <Image
                  src={data.empresa_logo_url}
                  alt="Logo empresa"
                  maxH="40px"
                  maxW="80px"
                  objectFit="contain"
                />
              );
            }
          } catch {
            /* ignore */
          }
          return null;
        })()}
        <Image src={logo} alt="Logo Especialistas" maxH="45px" objectFit="contain" />
      </Flex>

      {/* Menu Items */}
      <VStack spacing={1} align="stretch" p={4} flex={1}>
        {menuItems.map(item => {
          const active = isActive(item.path);

          return (
            <Flex
              key={item.path}
              align="center"
              gap={3}
              px={4}
              py={3}
              borderRadius="xl"
              cursor="pointer"
              bg={active ? 'blue.50' : 'transparent'}
              color={active ? 'blue.600' : 'gray.600'}
              fontWeight={active ? '600' : '500'}
              transition="all 0.2s"
              _hover={{
                bg: active ? 'blue.100' : 'gray.100',
                color: active ? 'blue.700' : 'gray.800',
                transform: 'translateX(4px)',
              }}
              onClick={() => handleNavigate(item.path)}
            >
              <Icon as={item.icon} boxSize={5} weight={active ? 'fill' : 'regular'} />
              <Text fontSize="sm">{item.name}</Text>
            </Flex>
          );
        })}
      </VStack>

      {/* Logout */}
      <Box p={4} borderTop="1px solid" borderTopColor="gray.100">
        <Flex
          align="center"
          gap={3}
          px={4}
          py={3}
          borderRadius="xl"
          cursor="pointer"
          color="red.500"
          fontWeight="500"
          transition="all 0.2s"
          _hover={{
            bg: 'red.50',
            transform: 'translateX(4px)',
          }}
          onClick={handleLogout}
        >
          <Icon as={SignOut} boxSize={5} />
          <Text fontSize="sm">Sair</Text>
        </Flex>
      </Box>
    </Flex>
  );
};

const SecaoClienteSidebar = ({
  isOpen = false,
  onClose = () => {},
  isMobile = false,
}: SecaoClienteSidebarProps) => {
  // Desktop Sidebar
  if (!isMobile) {
    return (
      <Box
        position="fixed"
        left={0}
        top={0}
        h="100vh"
        w="280px"
        bg="white"
        borderRight="1px solid"
        borderRightColor="gray.200"
        boxShadow="sm"
        zIndex={200}
        display={{ base: 'none', lg: 'flex' }}
        flexDirection="column"
      >
        <SidebarContent />
      </Box>
    );
  }

  // Mobile Drawer
  return (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
      <DrawerOverlay bg="blackAlpha.600" />
      <DrawerContent maxW="280px">
        <DrawerCloseButton
          size="lg"
          top={4}
          right={4}
          color="gray.500"
          _hover={{ color: 'gray.700' }}
        />
        <DrawerBody p={0}>
          <SidebarContent onClose={onClose} />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default SecaoClienteSidebar;
